import axios, { AxiosError } from 'axios'
import { ApiError, ApiErrorResponse } from './types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
const AUTH_TOKEN_KEY = 'authToken'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    delete apiClient.defaults.headers.common['Authorization']
  }
}

// Immediately try to load and set the token from localStorage
const storedToken = localStorage.getItem(AUTH_TOKEN_KEY)
if (storedToken) {
  setAuthToken(storedToken)
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (axios.isAxiosError(error)) {
      throw handleApiError(error as AxiosError<ApiErrorResponse>)
    }
    throw error
  },
)

const handleApiError = (error: AxiosError<ApiErrorResponse>): ApiError => {
  if (error.response?.data) {
    const errorData = error.response.data
    const firstError = errorData.errors?.[0]

    if (firstError) {
      return {
        code: firstError.code,
        message: firstError.message,
        details: {
          location: firstError.location,
          allErrors: errorData.errors,
        },
        correlationId: errorData.correlationId,
        httpStatusCode: errorData.httpStatusCode,
      }
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred',
      correlationId: errorData.correlationId,
      httpStatusCode: errorData.httpStatusCode,
    }
  }

  if (error.code === 'ERR_NETWORK') {
    return {
      code: 'NETWORK_ERROR',
      message: 'Network error. Please check your internet connection and try again.',
      httpStatusCode: 0,
    }
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred',
    httpStatusCode: error.response?.status,
  }
}

export default apiClient
